import * as Geometry from './Geometry';
import { Position, Size } from './Geometry';

/**
 * Contains helper methods to handle dealing with the visual and layout viewports.
 *
 * =========================================================================================================
 *
 * Background Information
 *
 * Roughly speaking, the visual viewport refers to the portion of the page that is displayed on the screen.
 * The layout viewport refers to the portion of the page that usually determines how fixed elements
 * are laid out. DOM geometries prefixed with "client" are relative to the layout viewport
 * (e.g. clientX/Y on pointer events, getBoundingClientRect()).
 * Although they are usually the same thing when fully zoomed out, when pinched-to-zoom the visual viewport
 * can become smaller than the layout viewport.
 * Some browsers, notably Chrome and Edge, allow the visual viewport to be scrolled independently of the
 * layout viewport, whereas others like Safari will always keep their origins at the same position on the page.
 *
 * See http://www.quirksmode.org/mobile/viewports2.html for diagrams. Some of the information here regarding
 * support for viewport-related properties is out of date so make sure to verify before using.
 */

// This represents the subset of the Visual Viewport API that we have a polyfill for.
interface VisualViewportPartial {
  readonly offsetLeft: number;
  readonly offsetTop: number;
  readonly pageLeft: number;
  readonly pageTop: number;
  readonly width: number;
  readonly height: number;
}

// TFSID 1162647: We want to use `window.top` whenever possible, but cross-origin frame sandboxing can prevent that
// TFSID 1192035: This broke more than it fixed, so temporarily reverting for now until we can figure out how to differentiate the cases
// export const topWindow: Window = function crossFrameSafeWindowGetter(): Window {
//   try {
//     if (window.top.innerWidth || true) {
//       return window.top;
//     }
//   } catch {
//     return window;
//   }
// }();

/**
 * Returns the element position relative to the origin of the layout viewport.
 */
function getElementPositionRelativeToLayoutViewport(element: HTMLElement): Position {
  return element.getBoundingClientRect() as Position;
}

/**
 * Returns the position of the layout viewport relative to the origin of the page.
 */
function getLayoutViewportPositionRelativeToPage(): Position {
  const documentClientRect = document.documentElement.getBoundingClientRect();
  return { top: -documentClientRect.top, left: -documentClientRect.left };
}

/**
 * Returns the size of the layout viewport.
 */
function getLayoutViewportSize(): Size {
  return { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight };
}

/**
 * Returns the rectangle representing the visual viewport in the layout viewport's coordinate system.
 * For browsers that do not support the visual viewport API, the rectangle will include the space taken
 * by scroll bars if present.
 */
function getVisualViewportRectRelativeToLayoutViewport(): ClientRect {
  const visualViewport = getVisualViewport();
  const pos = {
    left: visualViewport.offsetLeft,
    top: visualViewport.offsetTop
  };
  const size = {
    width: visualViewport.width,
    height: visualViewport.height
  };
  return Geometry.makeClientRect(pos, size);
}

class VisualViewportPolyfill implements VisualViewportPartial {
  public get offsetLeft(): number {
    const layoutPosInPageCoords = getLayoutViewportPositionRelativeToPage();
    return window.pageXOffset - layoutPosInPageCoords.left;
  }

  public get offsetTop(): number {
    const layoutPosInPageCoords = getLayoutViewportPositionRelativeToPage();
    return window.pageYOffset - layoutPosInPageCoords.top;
  }

  public get pageLeft(): number {
    return window.pageXOffset;
  }

  public get pageTop(): number {
    return window.pageYOffset;
  }

  // This polyfill doesn't work perfectly -- it will include the space taken by scroll bars if present
  public get width(): number {
    return window.innerWidth;
  }

  // This polyfill doesn't work perfectly -- it will include the space taken by scroll bars if present
  public get height(): number {
    return window.innerHeight;
  }
}

/**
 * Returns Window.visualViewport if the API is available, otherwise returns our polyfill.
 * Note that this only exposes the portion of the Visual Viewport API that we have polyfilled.
 * See https://github.com/WICG/ViewportAPI for details on the Visual Viewport API.
 */
function getVisualViewport(): VisualViewportPartial {
  return window.visualViewport ?? new VisualViewportPolyfill();
}

export {
  VisualViewportPartial,
  getElementPositionRelativeToLayoutViewport,
  getLayoutViewportPositionRelativeToPage,
  getLayoutViewportSize,
  getVisualViewportRectRelativeToLayoutViewport,
  getVisualViewport
};

